<template>
  <v-app>
    <v-content>
      <router-view/>
    </v-content>
    <v-snackbar v-model="snackBar.show" bottom>
      {{ snackBar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
          color="accent"
          text
          v-bind="attrs"
          @click="closeSnackBar()"
        >
          dismiss
        </v-btn>
      </template>
    </v-snackbar>

    <v-dialog v-model="unsavedChangesModal" width="680" persistent content-class="unsaved-changes-dialog">
      <v-card>
        <v-card-title class="headline pr-3 py-3">
          {{$t('unsaved_changes_title')}}
          <v-spacer></v-spacer>
          <v-btn icon @click="cancelLeave"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="py-4">
          {{$t('unsaved_changes_description')}}<br><br>
          {{$t('unsaved_changes_description_confirm')}}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="leaveWithoutPublishing" class="mr-2" text>{{$t('leave_without_publishing')}}</v-btn>
          <v-btn @click="publishAndLeave" color="primary" :loading="publishingFromModal">{{$t('publish_changes')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>

import { default as axios } from './utils/axios.instances' // eslint-disable-line
import { mapMutations, mapGetters } from 'vuex'
import amplitude from 'amplitude-js'
import Api from '@/api'
import '@scantrust/st-gallery/dist/index.css'

// The editor renders brand-authored HTML in preview iframes. That HTML is
// untrusted input, and a sandboxed frame can still postMessage this window —
// so any message that mutates auth state has to be proven to come from the
// portal shell that embeds the editor. Without this, a preview frame could
// post a token of its own choosing and every subsequent autosave would leave
// under it.
const PORTAL_ORIGINS = (Api.portal_origins || '')
  .split(',')
  .map(origin => origin.trim())
  .filter(Boolean)

function isFromPortalShell (event) {
  // window.parent is the portal that iframes us; a preview frame is never it.
  // When the editor runs standalone, window.parent === window, and a preview
  // frame is still not it.
  if (event.source !== window.parent) return false
  // Origin allowlist is opt-in: where VUE_APP_PORTAL_ORIGINS is unset the
  // source check above stands on its own rather than breaking the deployment.
  if (PORTAL_ORIGINS.length && PORTAL_ORIGINS.indexOf(event.origin) === -1) return false
  return true
}

export default {
  name: 'App',
  computed: {
    ...mapGetters(['currentPlan', 'editor', 'isSelfServe', 'snackBar', 'consumerOptions', 'isRefreshingToken', 'isTokenRefreshForced', 'hasUnpublishedChanges', 'saving', 'publishing', 'needToUpdateRedirection', 'published', 'defaultLanguage'])
  },
  data () {
    return {
      campaignLoaded: false,
      unsavedChangesModal: false,
      publishingFromModal: false
    }
  },
  methods: {
    ...mapMutations({
      enableRedirectionTab: 'ENABLE_REDIRECTION_TAB'
    }),

    closeSnackBar () {
      this.$store.dispatch('dissmissSnackBar')
    },

    save () {
      if (this.$route.matched && (this.$route.matched[0].name === 'edit-config' || this.$route.matched[0].name === 'setup')) {
        this.$store.dispatch('saveRemoteConfig')
      } else {
        this.$store.dispatch('updateStcEditor')
      }
    },

    debounce (callback, wait, immediate = false) {
      let timeout = null

      return function () {
        const callNow = immediate && !timeout
        const next = () => callback.apply(this, arguments)

        clearTimeout(timeout)
        timeout = setTimeout(next, wait)
        if (callNow) {
          next()
        }
      }
    },

    async handleMessage (event) {
      if (!isFromPortalShell(event)) return
      console.log('EVT RECEIVED STC3', event)
      if (event.data && event.data.type === 'token' && event.data.token) {
        this.$store.dispatch('setScantrustAuthToken', event.data.token)
        console.log('NEW TOKEN _>', event.data.token)
      } else if (event.data && event.data.type === 'tokenUpdateCompleted' && event.data.token) {
        this.$store.dispatch('setScantrustAuthToken', event.data.token)
        // only call process pending request when refreshing the token on 401 error
        if (this.isRefreshingToken) {
          this.$store.dispatch('processPendingRequest', event.data.token)
        }
        if (this.isTokenRefreshedForce) this.$store.dispatch('forceTokenRefresh', false)
      } else if (event.data && event.data.type === 'editor.beforeNavigate') {
        // Wait for any pending save or publish to complete before checking
        if (this.saving || this.publishing) {
          await new Promise((resolve) => {
            const unwatch = this.$store.watch(
              (state, getters) => getters.saving || getters.publishing,
              (busy) => {
                if (!busy) {
                  unwatch()
                  resolve()
                }
              }
            )
          })
        }

        // Prevent the modal from triggering if still in language setup page
        if (!this.editor.default_language) {
          parent.postMessage({ type: 'editor.readyToLeave' }, '*')
          return
        }

        if (!this.hasUnpublishedChanges) {
          parent.postMessage({ type: 'editor.readyToLeave' }, '*')
        } else {
          this.unsavedChangesModal = true
          // Tell the parent the modal is up so it can cancel its fallback timer and wait for the user's decision
          parent.postMessage({ type: 'editor.leavePrompted' }, '*')
        }
      }
    },

    cancelLeave () {
      this.unsavedChangesModal = false
      parent.postMessage({ type: 'editor.cancelLeave' }, '*')
    },

    leaveWithoutPublishing () {
      this.unsavedChangesModal = false
      parent.postMessage({ type: 'editor.readyToLeave' }, '*')
    },

    async publishAndLeave () {
      this.publishingFromModal = true
      try {
        if (this.needToUpdateRedirection) {
          await this.$store.dispatch('saveRedirectionSettings')
          parent.postMessage({ type: 'editor.update-redirection' }, '*')
        }
        await this.$store.dispatch('remotePublish', this.published.reviewed_languages?.includes(this.defaultLanguage) ? [] : [this.defaultLanguage])
        this.unsavedChangesModal = false
        parent.postMessage({ type: 'editor.readyToLeave' }, '*')
      } catch (e) {
        console.error('Publish failed', e)
      } finally {
        this.publishingFromModal = false
      }
    },

    async initialize () {
      await this.$store.dispatch('loadCampaign', { id: this.$route.query.campaign, mode: 'remote' })
      await this.$store.dispatch('loadLandingPageConfig', this.$route.params.id)
      this.campaignLoaded = true
      console.log('this.campaignLoaded:', this.campaignLoaded)

      // Tell Parent STC editor page has been loaded
      console.log('SENDING LOADED MSG')
      parent.postMessage({ type: 'editor.loaded' }, '*')
    },

    initAmplitude (user) {
      const identity = new amplitude.Identify().set('plan', this.currentPlan.id).set('email', user.email)
      amplitude.getInstance().setUserId(user.id)
      amplitude.getInstance().identify(identity)
    }

  },
  async created () {
    const debounceSave = this.debounce(() => {
      this.save()
    }, 2000, true)

    this.$store.watch(() => this.$store.getters.editor, (data) => {
      debounceSave()
    }, {
      deep: true
    })

    axios.initInterceptor(this.$store)
    // add event listener
    window.addEventListener('message', this.handleMessage, false)

    if (Api.amplitude_key) {
      amplitude.getInstance().init(Api.amplitude_key)
      amplitude.getInstance().options.apiEndpoint = 'api2.amplitude.com'
    }

    const token = this.$route.query.token || Api.uat_token
    if (token) {
      this.$store.dispatch('setScantrustAuthToken', token)
    }
    if (this.$route.query.redirect) {
      console.log('enable redirect')
      this.enableRedirectionTab()
    }
    const user = await this.$store.dispatch('loadUserProfile')

    if (this.$route.query.product) {
      await this.$store.dispatch('loadCurrentProduct', this.$route.query.product)
    }

    if (this.isSelfServe) {
      this.$vuetify.theme.themes.light.primary = '#0070B6'
      document.documentElement.style.setProperty('--primary', '0,112,182')
    }

    this.initAmplitude(user)
    await this.initialize()

    if (!this.editor.default_language) {
      this.$router.push({ name: 'setup' })
    }

    if (this.$route.query.view) {
      this.$router.push({ name: this.$route.query.view })
    }
  },

  beforeDestroy () {
    window.removeEventListener('message', this.handleMessage)
  }
}

</script>

<style lang="sass">
// Bump above InfoTooltip 10000, Preview 9999 so the modal isn't hidden behind them.
.v-dialog__content:has(> .unsaved-changes-dialog)
  z-index: 10002 !important
</style>
